import { invalidate } from '@react-three/fiber';
import { t } from 'i18next';
import { Color, MathUtils, Matrix4, Object3D, Quaternion, Vector3 } from 'three';

import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import {
    getEffectiveDistanceSq,
    getLodLevelByDistanceSq
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleLodUtils';
import {
    calculateFullQuaternion,
    calculateRotation,
    updateFullQuaternion,
    mapPartsRecordToArray,
    cloneArticleInstances,
    convertToRadiant,
    convertToDegree,
    isFramiClamp,
    findPoiGuidsByInstanceId
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    PANEL_REPLACEMENT_ACTIVE_OPACITY,
    setBatchInstanceOpacity,
    updateBatchedMeshInstance,
    updateAttributeRange
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import {
    getPoiNormal,
    getAnchorPartPosition,
    getClipboardCentroid,
    getInsertionPoint,
    getPasteOffset,
    isMultiPOISelection,
    finalizePaste,
    buildPastePayload,
    getNearestSnapCandidate,
    applyCornerPaste,
    clonePartPlacementsWithZOffset,
    getPlacementAnchorPart
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/clipboardUtils';
import {
    dragColliderCache,
    isTopBottomCameraView,
    raycastPlane,
    getConnectorArticleId,
    getManualPlacementPlane,
    getPreferredInsertionPlane,
    updateCursorRay
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { getDimensionHiddenSheetArticles } from 'app/modules/wallforming/components/scenes/objects/articles/utils/sheetVisibilityUtils';
import {
    applyScalingToAllInstances,
    applyRotationToAllInstances,
    applyTranslationToAllInstances,
    collectUpdatedSelectedInstances,
    createIndexLookup,
    decomposeRollAroundAxis,
    deltaVector,
    getSelectedScaleFactor,
    getSelectionTransformationFrame,
    getMovementLabel,
    getFillerDimensionPerScaleFactor,
    getSnappingRotation,
    indexLookupCache,
    isSingleFillerInstanceSelection,
    lockedTranslationDir,
    newPosition,
    prevPosition,
    clampScaleFactor,
    rotDeltaQuat,
    rotNewAlignment,
    rotNewAlignQuat,
    rotPartQuat,
    rotRollQuat,
    snappingPosition,
    updateInstanceMatricesForSelection,
    updateSnappingPosition
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { TransformationMode } from 'app/modules/wallforming/components/scenes/objects/helpers/types/transformationMode';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { getRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { EMPTY_GUID, EPSILON } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { PartPlacement } from 'api/models';
import type { ArticleClipboardEntry } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleClipboardEntry';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';
import type { ArticleTransformationSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleTransformationSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const scaleVisible = new Vector3(1, 1, 1);
const scaleHidden = new Vector3(0, 0, 0);
const tmpMatrix = new Matrix4();
const selectionColor = new Color(0x66b3ff);
const defaultColor = new Color(0xffffff);

// Cache articleInstanceSelections during drag
const selectionSetCache = new WeakMap<object, Set<string>>();

const newQuaternion = new Quaternion();
const newScale = new Vector3();
const tmpQuaternion = new Quaternion();
const anchorAlignmentCache = new Vector3();
const anchorPoint = new Vector3();

const projectedPoint = new Vector3();

const movementThreshold = 0.0001;

let isTransformationCancelled = false;
let prevAnchorRotation = 0;
let currentTransformationRotation = 0;
let scalingAxis: 0 | 1 | 2 | null = null;
let currentScalingFactor = 1;
let scalingBaseFactor = 1;
let scalingDimensionPerScaleFactor = 1;

let hasLockedTranslationDir = false;

const pendingColliders: ColliderPoint[] = [];
const pendingRelationIds = new Set<string>();
let colliderFlushScheduled = false;

function queueArticleColliderUpdate(
    colliders: ColliderPoint[],
    relationIds: Set<string>,
    get: () => PlacementState
): void {
    for (const id of relationIds) {
        pendingRelationIds.add(id);
    }
    for (const c of colliders) {
        pendingColliders.push(c);
    }

    if (colliderFlushScheduled) {
        return;
    }
    colliderFlushScheduled = true;

    queueMicrotask(() => {
        colliderFlushScheduled = false;
        if (pendingColliders.length === 0) {
            return;
        }

        const batch = pendingColliders.splice(0);
        const relations = new Set(pendingRelationIds);
        pendingRelationIds.clear();
        get().batchSetArticleColliders(batch, relations);
    });
}

export const createArticleTransformationSlice: StateCreator<PlacementState, [], [], ArticleTransformationSliceState> = (
    set,
    get
) => ({
    articleTransformationMode: TransformationMode.NONE,
    setArticleTransformationMode: (mode) => set({ articleTransformationMode: mode }),

    transformationLabel: 0,
    setTransformationLabel: (label) =>
        set({
            transformationLabel: label
        }),

    updateInstanceMatrices: (
        showSelection,
        articleRef,
        updateColliders,
        articleInstanceIds,
        isArticleVisible,
        hoveredArticleInstanceIds
    ) => {
        if (!articleRef.current) {
            return;
        }

        const allInstanceIds = Object.keys(articleRef.current.articleInstances);
        const instanceIds = articleInstanceIds ? [...new Set(articleInstanceIds)] : allInstanceIds;
        const articleId = articleRef.current.id;
        const state = get();
        if (!state || !articleId) {
            return;
        }

        const instanceCount = allInstanceIds.length;
        let indexLookup: Map<string, number>;

        // Track lookup source to refresh stale cache if needed
        let indexLookupSource: Record<string, ArticleInstance> | null = null;

        const cachedLookup = indexLookupCache.get(articleId);
        if (
            cachedLookup &&
            cachedLookup.instanceIds.length === instanceCount &&
            cachedLookup.instanceIds.every((instanceId, index) => instanceId === allInstanceIds[index])
        ) {
            indexLookup = cachedLookup.lookup;
        } else {
            indexLookup = createIndexLookup(articleId, allInstanceIds);
            indexLookupSource = articleRef.current.articleInstances;
        }

        let selectionSet = selectionSetCache.get(state.articleInstanceSelections);
        if (!selectionSet) {
            selectionSet = new Set(Object.keys(state.articleInstanceSelections));
            selectionSetCache.set(state.articleInstanceSelections, selectionSet);
        }
        const hoveredSet = hoveredArticleInstanceIds ? new Set(hoveredArticleInstanceIds) : undefined;

        let poiLookup: Map<string, Set<string>> | undefined;
        let poiIndexByGuid: Map<string, number> | undefined;
        if (updateColliders) {
            poiLookup = new Map();
            poiIndexByGuid = new Map();
            state.pois.forEach((poiData, poiIndex) => {
                poiIndexByGuid!.set(poiData.poi.poiGuid, poiIndex);
                const articleInstanceIds = poiData.articles?.[articleId];
                if (Array.isArray(articleInstanceIds)) {
                    for (const instanceId of articleInstanceIds) {
                        const poiGuids = poiLookup!.get(instanceId) ?? new Set<string>();
                        poiGuids.add(poiData.poi.poiGuid);
                        poiLookup!.set(instanceId, poiGuids);
                    }
                }
            });
        }

        const colliderBatch: ColliderPoint[] = [];
        const colliderRelationIds = new Set<string>();

        const isMainCanvasBatch = state.isBatchModeActive() && articleRef.current.isMainCanvas;
        const isNavigatorBatch = state.isNavigatorBatchModeActive() && !articleRef.current.isMainCanvas;
        const mainCamera = state.mainCameraControlsRef.current?.camera;

        const requiredPartKeys = state.articles[articleId]?.partNames ?? [];
        if (requiredPartKeys.length > 0) {
            const hasMissingParts = instanceIds.some((instanceId) => {
                const instance = articleRef.current?.articleInstances[instanceId];
                if (!instance) {
                    return false;
                }

                return requiredPartKeys.some((partKey) => !(partKey in instance.parts));
            });

            if (hasMissingParts) {
                state.syncArticleInstanceParts(articleId, requiredPartKeys);

                const syncedInstances = cloneArticleInstances(get().articles[articleId]);
                if (syncedInstances && articleRef.current) {
                    articleRef.current.articleInstances = syncedInstances;
                }
            }
        }

        // Track updated indices for partial buffer updates
        const updatedPhysical = new Set<number>();
        const updatedSheet = new Set<number>();
        const updatedHoles = new Set<number>();
        const hiddenInstanceIds = state.hiddenInstanceIds;
        const dimensionHiddenSheetArticles = isMainCanvasBatch ? getDimensionHiddenSheetArticles(state) : undefined;

        instanceIds.forEach((instanceId) => {
            if (!articleRef.current || !articleRef.current.articleInstances[instanceId]) {
                return;
            }

            // compute properties once per instance
            let index = indexLookup.get(instanceId);
            if (index === undefined) {
                // Rebuild lookup if source has changed - prevents skipping instances
                if (indexLookupSource === articleRef.current.articleInstances) {
                    return;
                }

                indexLookupSource = articleRef.current.articleInstances;
                indexLookup = createIndexLookup(articleId, Object.keys(indexLookupSource));
                index = indexLookup.get(instanceId);
                if (index === undefined) {
                    return;
                }
            }

            if (selectionSet.has(instanceId)) {
                if (state.articleInstanceSelections[instanceId] !== index) {
                    state.updateArticleInstanceSelection(instanceId, index);
                }
            }

            const instance = articleRef.current.articleInstances[instanceId];
            const isArticleInstanceSelected = selectionSet.has(instanceId);
            const isArticleInstanceHovered = hoveredSet?.has(instanceId) ?? false;
            const isInstanceHidden = hiddenInstanceIds.has(instanceId);
            const bodyVisible = isArticleVisible !== undefined ? isArticleVisible : true;
            const shouldRenderVisible = bodyVisible && !isInstanceHidden;
            const shouldShowSelection = showSelection && !isInstanceHidden;
            const lodAnchorPart = Object.values(instance.parts)[0];
            const lodLevel = isNavigatorBatch
                ? 2
                : mainCamera && lodAnchorPart
                  ? getLodLevelByDistanceSq(
                        getEffectiveDistanceSq(mainCamera, mainCamera.position, lodAnchorPart.position)
                    )
                  : 0;

            if (isMainCanvasBatch) {
                const batchIds = state.instanceBatchIds.get(instanceId);
                const slots = batchIds ? state.articleGeometrySlots.get(batchIds.articleId) : undefined;
                if (batchIds && slots) {
                    const { physical: physicalBatch, sheet: sheetBatch, holes: holesBatch } = state.batchedMeshes;
                    // Use shared utility
                    updateBatchedMeshInstance({
                        physicalBatch,
                        sheetBatch,
                        holesBatch,
                        batchIds,
                        slots,
                        instance,
                        showSelection: shouldShowSelection,
                        isArticleInstanceSelected,
                        isArticleInstanceHovered,
                        isVisible: shouldRenderVisible,
                        selectionColor,
                        placementOpacity:
                            state.isPanelQuickReplacementOpen && state.isPanelQuickReplacementGridActive
                                ? PANEL_REPLACEMENT_ACTIVE_OPACITY
                                : undefined,
                        hideSheet:
                            (state.isPanelQuickReplacementOpen && state.isPanelQuickReplacementGridActive) ||
                            (dimensionHiddenSheetArticles?.[articleId]?.includes(instanceId) ?? false),
                        defaultColor,
                        tmpQuaternion,
                        tmpMatrix,
                        scaleHidden,
                        updatedPhysical,
                        updatedSheet,
                        updatedHoles,
                        lodLevel,
                        setBatchInstanceOpacity
                    });
                }
            } else if (isNavigatorBatch) {
                const batchIds = state.navigatorInstanceBatchIds.get(instanceId);
                const slots = batchIds ? state.navigatorGeometrySlots.get(batchIds.articleId) : undefined;
                if (batchIds && slots) {
                    const { physical, sheet, holes } = state.navigatorBatchedMeshes;
                    updateBatchedMeshInstance({
                        physicalBatch: physical,
                        sheetBatch: sheet,
                        holesBatch: holes,
                        batchIds,
                        slots,
                        instance,
                        showSelection: shouldShowSelection,
                        isArticleInstanceSelected,
                        isArticleInstanceHovered,
                        isVisible: shouldRenderVisible,
                        selectionColor,
                        defaultColor,
                        tmpQuaternion,
                        tmpMatrix,
                        scaleHidden,
                        updatedPhysical,
                        updatedSheet,
                        updatedHoles,
                        lodLevel
                    });
                }
            }

            // update colliders
            if (updateColliders && articleRef.current && poiLookup && poiIndexByGuid) {
                const poiGuids = poiLookup.get(instanceId);
                const primaryPoiGuid = instance.poiGuids?.[0] ?? poiGuids?.values().next().value;
                const poiIndex = primaryPoiGuid ? (poiIndexByGuid.get(primaryPoiGuid) ?? -1) : -1;

                if (poiIndex !== -1 && poiGuids) {
                    const newColliders = state.computeInstanceColliders(
                        instanceId,
                        instance,
                        articleRef.current.articleRepresentations,
                        poiIndex,
                        [...poiGuids]
                    );
                    if (newColliders.length > 0) {
                        colliderRelationIds.add(instanceId);
                        colliderBatch.push(...newColliders);
                    }
                }
            }
        });

        // For each batch, update only the changed range for each attribute
        if (state.batchedMeshes.physical) {
            updateAttributeRange(state.batchedMeshes.physical, updatedPhysical, 'matrixAttribute', 16);
            updateAttributeRange(state.batchedMeshes.physical, updatedPhysical, 'colorAttribute', 4);
            updateAttributeRange(state.batchedMeshes.physical, updatedPhysical, 'visibleAttribute', 1);
            // Opacity is in _colorsTexture, which is updated in setBatchInstanceOpacity
        }
        if (state.batchedMeshes.sheet) {
            updateAttributeRange(state.batchedMeshes.sheet, updatedSheet, 'matrixAttribute', 16);
            updateAttributeRange(state.batchedMeshes.sheet, updatedSheet, 'visibleAttribute', 1);
        }
        if (state.batchedMeshes.holes) {
            updateAttributeRange(state.batchedMeshes.holes, updatedHoles, 'matrixAttribute', 16);
            updateAttributeRange(state.batchedMeshes.holes, updatedHoles, 'visibleAttribute', 1);
        }

        if (colliderBatch.length > 0) {
            queueArticleColliderUpdate(colliderBatch, colliderRelationIds, get);
        }

        invalidate();
    },

    updateInstanceVisibility: (instanceIds, isVisible) => {
        if (!instanceIds || instanceIds.length === 0) {
            return;
        }

        const state = get();
        const uniqueInstanceIds = [...new Set(instanceIds)];
        let hasChanges = false;

        for (const instanceId of uniqueInstanceIds) {
            const isHidden = state.hiddenInstanceIds.has(instanceId);
            if (isVisible) {
                if (state.hiddenInstanceIds.delete(instanceId)) {
                    hasChanges = true;
                }
            } else if (!isHidden) {
                state.hiddenInstanceIds.add(instanceId);
                hasChanges = true;
            }
        }

        if (!hasChanges) {
            return;
        }

        for (const articleId of Object.keys(state.articles)) {
            const articleRef = state.articleDragRefsMap.current.get(articleId)?.articleRef;
            if (articleRef?.current) {
                state.updateInstanceMatrices(true, articleRef, false, uniqueInstanceIds);
            }
        }

        requestAnimationFrame(() => invalidate());
    },

    updatePivotControls: () => {
        const state = get();
        if (state.isDraggingActive() || state.isViewMode) {
            return;
        }

        const pivotControlsActive = state.arePivotControlsActive;
        state.setArePivotControlsActive(!pivotControlsActive);
    },

    updateTransformationMode: (worldMatrix, selectedControl) => {
        const state = get();
        if (state.articleTransformationMode !== TransformationMode.NONE) {
            return;
        }

        worldMatrix.decompose(newPosition, newQuaternion, newScale);

        if (selectedControl?.component === 'Sphere') {
            const factor = newScale.getComponent(selectedControl.axis);
            if (Math.abs(factor - 1) > movementThreshold) {
                scalingAxis = selectedControl.axis;
                currentScalingFactor = factor;
                state.setArticleTransformationMode(TransformationMode.SCALING);
            }
            return;
        }

        const positionChanged = newPosition.distanceTo(prevPosition) > movementThreshold;
        if (positionChanged) {
            state.setArticleTransformationMode(TransformationMode.TRANSLATION);

            if (!hasLockedTranslationDir) {
                lockedTranslationDir.subVectors(newPosition, prevPosition).normalize();
                hasLockedTranslationDir = true;
            }
            return;
        }

        const rotationChanged = 1 - Math.abs(newQuaternion.dot(tmpQuaternion)) > EPSILON;
        if (rotationChanged) {
            state.setArticleTransformationMode(TransformationMode.ROTATION);
        }
    },

    updateTransformationMatrix: (worldMatrix, snappingRotation, selectedControl) => {
        const state = get();
        if (!state.pivotControlsRef.current) {
            return;
        }

        worldMatrix.decompose(newPosition, newQuaternion, newScale);
        state.updateTransformationMode(worldMatrix, selectedControl);

        if (get().articleTransformationMode === TransformationMode.ROTATION) {
            const rotation = calculateRotation(worldMatrix, anchorAlignmentCache);
            snappingRotation.current = getSnappingRotation(rotation);
            currentTransformationRotation = snappingRotation.current;
            snappingPosition.copy(prevPosition);
        } else if (get().articleTransformationMode === TransformationMode.TRANSLATION) {
            updateSnappingPosition(hasLockedTranslationDir);
            snappingRotation.current = prevAnchorRotation;
        } else if (get().articleTransformationMode === TransformationMode.SCALING && scalingAxis !== null) {
            currentScalingFactor = clampScaleFactor(scalingBaseFactor * Math.abs(newScale.getComponent(scalingAxis)));
            const scalingRatio = currentScalingFactor / scalingBaseFactor;
            state.setTransformationLabel(scalingDimensionPerScaleFactor * currentScalingFactor);
            snappingPosition.copy(prevPosition);
            snappingRotation.current = prevAnchorRotation;

            const pivot = state.pivotControlsRef.current;
            if (pivot) {
                pivot.updateWorldMatrix(true, false);
                const direction = new Vector3().setFromMatrixColumn(pivot.matrixWorld, scalingAxis).normalize();
                const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
                applyScalingToAllInstances(
                    scalingAxis,
                    scalingRatio,
                    prevPosition,
                    direction,
                    selectedInstanceIds,
                    state
                );
                updateInstanceMatricesForSelection(selectedInstanceIds, state, false);
            }
        }

        if (get().articleTransformationMode !== TransformationMode.NONE) {
            const snappingQuaternion = calculateFullQuaternion(anchorAlignmentCache, snappingRotation.current);
            state.pivotControlsRef.current.matrix.compose(snappingPosition, snappingQuaternion, scaleVisible);
            state.pivotControlsRef.current.matrixWorldNeedsUpdate = true;
        }
    },

    handleOnArticleTransformationStart: (prevRadians, positionUpdated, selectedControl) => {
        const state = get();
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        if (state.isViewMode || selectedInstanceIds.length === 0) {
            return;
        }

        isTransformationCancelled = false;
        state.setIsTransformationActiveRef(true);
        state.setIsArticleDirectlySelectedRef(true);

        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return;
        }

        state.beginHistoryTransaction('historyTransform');
        anchorAlignmentCache.copy(frame.alignment);
        prevAnchorRotation = frame.rotation;
        prevPosition.copy(frame.position);
        if (state.pivotControlsRef.current) {
            state.pivotControlsRef.current.matrix.compose(frame.position, frame.quaternion, scaleVisible);
            state.pivotControlsRef.current.matrixWorldNeedsUpdate = true;
        }

        prevRadians.current = prevAnchorRotation;
        currentTransformationRotation = prevAnchorRotation;
        positionUpdated.current = false;
        state.setArticleTransformationMode(TransformationMode.NONE);
        hasLockedTranslationDir = false;
        scalingAxis = selectedControl?.component === 'Sphere' ? selectedControl.axis : null;
        scalingBaseFactor =
            scalingAxis === null ? 1 : getSelectedScaleFactor(scalingAxis, selectedInstanceIds, state) || 1;
        currentScalingFactor = scalingBaseFactor;
        if (scalingAxis !== null) {
            const dimensionPerScaleFactor = getFillerDimensionPerScaleFactor(scalingAxis, selectedInstanceIds, state);
            if (dimensionPerScaleFactor === undefined) {
                toastNotifications.information({
                    title: t('info'),
                    message: t('infoFillerDimensionsUnavailable')
                });
                scalingAxis = null;
            } else {
                scalingDimensionPerScaleFactor = dimensionPerScaleFactor;
                state.setTransformationLabel(scalingDimensionPerScaleFactor * scalingBaseFactor);
            }
        }
        updateFullQuaternion(anchorAlignmentCache, prevAnchorRotation, tmpQuaternion);
        snappingPosition.copy(prevPosition);
    },

    handleOnArticleTransformationEnd: (isDragEnd) => {
        const state = get();
        if (state.isViewMode) {
            return;
        }
        isTransformationCancelled = false;
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const transformationMode = state.articleTransformationMode;

        state.setIsTransformationActiveRef(false);
        state.setIsDragActiveRef(false);

        if (selectedInstanceIds.length === 0) {
            state.setArticleTransformationMode(TransformationMode.NONE);
            scalingAxis = null;
            currentScalingFactor = 1;
            scalingBaseFactor = 1;
            scalingDimensionPerScaleFactor = 1;
            return;
        }

        if (isDragEnd && transformationMode === TransformationMode.TRANSLATION) {
            deltaVector.subVectors(snappingPosition, prevPosition);
            const localDelta = deltaVector.clone().applyQuaternion(
                calculateFullQuaternion(anchorAlignmentCache, prevAnchorRotation).invert()
            );
            state.setHistoryTransformDetail({ kind: 'translation', vector: [localDelta.x, localDelta.y, localDelta.z] });
            applyTranslationToAllInstances(deltaVector, selectedInstanceIds, state);
        }

        if (isDragEnd && transformationMode === TransformationMode.ROTATION) {
            const deltaRotation = currentTransformationRotation - prevAnchorRotation;
            state.setHistoryTransformDetail({
                kind: 'rotation',
                degrees: MathUtils.radToDeg(deltaRotation),
                axis: [0, 0, 1]
            });
            applyRotationToAllInstances(deltaRotation, prevPosition, anchorAlignmentCache, selectedInstanceIds, state);
        }

        if (
            isDragEnd &&
            transformationMode === TransformationMode.SCALING &&
            scalingAxis !== null &&
            state.pivotControlsRef.current
        ) {
            const pivot = state.pivotControlsRef.current;
            pivot.updateWorldMatrix(true, false);
            const direction = new Vector3().setFromMatrixColumn(pivot.matrixWorld, scalingAxis).normalize();
            state.setHistoryTransformDetail({
                kind: 'scaling',
                axis: scalingAxis,
                dimensionMeters: scalingDimensionPerScaleFactor * currentScalingFactor
            });
            applyScalingToAllInstances(
                scalingAxis,
                currentScalingFactor / scalingBaseFactor,
                prevPosition,
                direction,
                selectedInstanceIds,
                state
            );
        }

        if (isDragEnd && transformationMode !== TransformationMode.NONE) {
            updateInstanceMatricesForSelection(selectedInstanceIds, state);

            const updatedInstances = collectUpdatedSelectedInstances(selectedInstanceIds, state);
            if (updatedInstances.length > 0) {
                state.updateArticleInstances(updatedInstances, false);
            }
            if (transformationMode === TransformationMode.SCALING) {
                state.toggleRecomputeTransformationBox?.();
            }
        }

        state.setArticleTransformationMode(TransformationMode.NONE);
        scalingAxis = null;
        currentScalingFactor = 1;
        scalingBaseFactor = 1;
        scalingDimensionPerScaleFactor = 1;
        invalidate();
    },

    handleOnArticleTransformation: (
        _localMatrix,
        _deltaLocal,
        worldMatrix,
        snappingRotation,
        positionUpdated,
        selectedControl
    ) => {
        if (isTransformationCancelled) {
            return false;
        }

        const state = get();
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        if (state.isViewMode || !state.pivotControlsRef.current || selectedInstanceIds.length === 0) {
            return false;
        }

        state.updateTransformationMatrix(worldMatrix, snappingRotation, selectedControl);

        if (get().articleTransformationMode === TransformationMode.NONE) {
            return true;
        }

        if (get().articleTransformationMode === TransformationMode.TRANSLATION) {
            deltaVector.subVectors(snappingPosition, prevPosition);

            const localDelta = deltaVector.clone().applyQuaternion(
                calculateFullQuaternion(anchorAlignmentCache, prevAnchorRotation).invert()
            );
            const deltaX = localDelta.x;
            const deltaY = localDelta.y;
            const deltaZ = localDelta.z;

            if (
                Math.abs(deltaX) > movementThreshold ||
                Math.abs(deltaY) > movementThreshold ||
                Math.abs(deltaZ) > movementThreshold
            ) {
                positionUpdated.current = true;
                const directionX = deltaX >= 0 ? '+' : '-';
                const directionY = deltaY >= 0 ? '+' : '-';
                const directionZ = deltaZ >= 0 ? '+' : '-';

                get().setTransformationLabel(
                    Math.abs(deltaX) > movementThreshold
                        ? getMovementLabel(directionX, Math.abs(deltaX))
                        : Math.abs(deltaY) > movementThreshold
                          ? getMovementLabel(directionY, Math.abs(deltaY))
                          : getMovementLabel(directionZ, Math.abs(deltaZ))
                );
            } else {
                get().setTransformationLabel(0);
            }
        }

        if (get().articleTransformationMode === TransformationMode.ROTATION) {
            currentTransformationRotation = snappingRotation.current;

            if (!positionUpdated.current) {
                const degrees = convertToDegree(snappingRotation.current);
                get().setTransformationLabel(degrees);
            }
        }
        invalidate();
        return true;
    },

    applyTranslationValue: (axis, value) => {
        const state = get();

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        if (selectedInstanceIds.length === 0 || !state.pivotControlsRef.current) {
            return;
        }

        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return;
        }
        const localDelta = new Vector3().setComponent(axis, value);
        const deltaVector = localDelta.clone().applyQuaternion(frame.quaternion);
        state.setHistoryTransformDetail({ kind: 'translation', vector: [localDelta.x, localDelta.y, localDelta.z] });
        state.pivotControlsRef.current.matrix.compose(
            frame.position.clone().add(deltaVector),
            frame.quaternion,
            scaleVisible
        );
        state.pivotControlsRef.current.matrixWorldNeedsUpdate = true;

        applyTranslationToAllInstances(deltaVector, selectedInstanceIds, state);
        updateInstanceMatricesForSelection(selectedInstanceIds, state);

        const updated = collectUpdatedSelectedInstances(selectedInstanceIds, state);
        if (updated.length > 0) {
            state.updateArticleInstances(updated, false);
        }

        invalidate();
    },

    applyScalingValue: (axis, dimensionMeters) => {
        const state = get();
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const pivot = state.pivotControlsRef.current;
        if (
            selectedInstanceIds.length === 0 ||
            !pivot ||
            !isSingleFillerInstanceSelection(selectedInstanceIds, state)
        ) {
            return;
        }

        const dimensionPerScaleFactor = getFillerDimensionPerScaleFactor(axis, selectedInstanceIds, state);
        if (dimensionPerScaleFactor === undefined) {
            toastNotifications.information({ title: t('info'), message: t('infoFillerDimensionsUnavailable') });
            return;
        }
        if (!Number.isFinite(dimensionMeters) || dimensionMeters <= 0) {
            toastNotifications.information({ title: t('info'), message: t('infoScaleInputValidation') });
            return;
        }

        const currentScale = getSelectedScaleFactor(axis, selectedInstanceIds, state);
        const targetScale = dimensionMeters / dimensionPerScaleFactor;
        if (targetScale < EPSILON) {
            toastNotifications.information({ title: t('info'), message: t('infoScaleInputValidation') });
            return;
        }
        const scalingRatio = targetScale / (currentScale || 1);
        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return;
        }
        const direction = new Vector3().setComponent(axis, 1).applyQuaternion(frame.quaternion);
        applyScalingToAllInstances(axis, scalingRatio, frame.position, direction, selectedInstanceIds, state);
        updateInstanceMatricesForSelection(selectedInstanceIds, state);

        const updated = collectUpdatedSelectedInstances(selectedInstanceIds, state);
        if (updated.length > 0) {
            state.updateArticleInstances(updated, false);
        }

        state.setTransformationLabel(dimensionPerScaleFactor * targetScale);
        state.setHistoryTransformDetail({ kind: 'scaling', axis, dimensionMeters });
        state.toggleRecomputeTransformationBox?.();
        invalidate();
    },

    applyRotationValue: (_axis, degrees, snappingRotation) => {
        const state = get();

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        if (selectedInstanceIds.length === 0) {
            return;
        }

        const pivot = state.pivotControlsRef.current;
        if (!pivot) {
            return;
        }

        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return;
        }
        const targetRadians = convertToRadiant(degrees);
        const quaternion = calculateFullQuaternion(frame.alignment, targetRadians);
        const pos = frame.position;
        const scale = new Vector3(1, 1, 1);

        pivot.matrix.compose(pos, quaternion, scale);
        pivot.matrixWorldNeedsUpdate = true;

        const deltaRotation = targetRadians - frame.rotation;
        state.setHistoryRepeat('historyTransform', () => {
            const currentFrame = getSelectionTransformationFrame(get());
            if (currentFrame) {
                get().applyRotationValue(_axis, convertToDegree(currentFrame.rotation + deltaRotation), { current: 0 });
            }
        });
        anchorAlignmentCache.copy(frame.alignment);
        prevPosition.copy(frame.position);
        prevAnchorRotation = targetRadians;
        currentTransformationRotation = targetRadians;
        snappingRotation.current = targetRadians;

        const normal = frame.alignment.clone().normalize();
        state.setHistoryTransformDetail({
            kind: 'rotation',
            degrees: MathUtils.radToDeg(deltaRotation),
            axis: [0, 0, 1]
        });

        applyRotationToAllInstances(deltaRotation, prevPosition, normal, selectedInstanceIds, state);
        updateInstanceMatricesForSelection(selectedInstanceIds, state);

        const updated = collectUpdatedSelectedInstances(selectedInstanceIds, state);
        if (updated.length > 0) {
            state.updateArticleInstances(updated, false);
        }

        state.toggleRecomputeTransformationBox?.();
        invalidate();
    },

    cancelArticleTransformation: () => {
        isTransformationCancelled = true;
        const state = get();
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const hadActiveTransformation = state.articleTransformationMode !== TransformationMode.NONE;

        // restore properties of all selected instances
        for (const instanceId of selectedInstanceIds) {
            const dragRef = state.getSelectedArticleDragReference(instanceId);
            if (!dragRef?.articleRef.current) {
                continue;
            }
            const articleId = dragRef.articleRef.current.id;
            const articleInstance = state.articles[articleId]?.articleInstances[instanceId];
            if (!articleInstance) {
                continue;
            }

            for (const [name, part] of Object.entries(articleInstance.parts)) {
                const currentPart = dragRef.articleRef.current.articleInstances[instanceId].parts[name];
                if (!currentPart) {
                    continue;
                }

                currentPart.position.copy(part.position);
                currentPart.alignment.copy(part.alignment);
                currentPart.scale.copy(part.scale);
                currentPart.rotation = part.rotation;
            }
        }

        if (state.articleTransformationMode === TransformationMode.SCALING) {
            updateInstanceMatricesForSelection(selectedInstanceIds, state, false);
        }

        // reset pivot controls to original position
        if (state.arePivotControlsActive && state.pivotControlsRef.current && selectedInstanceIds.length > 0) {
            const label =
                state.articleTransformationMode === TransformationMode.ROTATION
                    ? convertToDegree(prevAnchorRotation)
                    : state.articleTransformationMode === TransformationMode.SCALING
                      ? scalingDimensionPerScaleFactor * scalingBaseFactor
                      : 0;
            get().setTransformationLabel(label);
            state.setArticleTransformationMode(TransformationMode.NONE);
            const quaternion = calculateFullQuaternion(anchorAlignmentCache, prevAnchorRotation);
            state.pivotControlsRef.current.matrix.compose(prevPosition, quaternion, scaleVisible);
            state.pivotControlsRef.current.matrixWorldNeedsUpdate = true;
        }

        scalingAxis = null;
        currentScalingFactor = 1;
        scalingBaseFactor = 1;
        scalingDimensionPerScaleFactor = 1;
        state.setIsTransformationActiveRef(false);
        state.setIsDragActiveRef(false);
        if (hadActiveTransformation) {
            state.setIsArticleDirectlySelectedRef(false);
        }
        state.handleOnDragArticleEnd(true);
        invalidate();
    },

    handleOnArticleDefaultRotation: () => {
        const state = get();
        if (state.isViewMode) {
            return;
        }
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        if (selectedInstanceIds.length === 0) {
            toastNotifications.information({ title: t('info'), message: t('infoRotation') });
            return;
        }

        const rotationStep = -Math.PI / 2;
        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return;
        }
        const rotationAxis = frame.alignment;
        const middlePoint = frame.position;
        state.setHistoryTransformDetail({
            kind: 'rotation',
            degrees: MathUtils.radToDeg(rotationStep),
            axis: [0, 0, 1]
        });

        // apply rotation to all selected instances, batch by articleId
        const grouped = new Map();
        rotDeltaQuat.setFromAxisAngle(rotationAxis, rotationStep);

        for (const instanceId of selectedInstanceIds) {
            const dragRef = state.getSelectedArticleDragReference(instanceId);
            if (!dragRef?.articleRef.current) {
                continue;
            }
            const articleId = dragRef.articleRef.current.id;
            const instance = dragRef.articleRef.current.articleInstances[instanceId];
            if (!instance) {
                continue;
            }

            for (const part of Object.values(instance.parts)) {
                const relPos = new Vector3().copy(part.position).sub(middlePoint);
                relPos.applyAxisAngle(rotationAxis, rotationStep);
                part.position.copy(middlePoint).add(relPos);

                // box rotation: rotate alignment, decompose new roll quaternion
                updateFullQuaternion(part.alignment, part.rotation, rotPartQuat);

                rotPartQuat.premultiply(rotDeltaQuat);
                rotNewAlignment.copy(part.alignment).applyQuaternion(rotDeltaQuat);
                part.alignment.copy(rotNewAlignment);
                updateFullQuaternion(rotNewAlignment, 0, rotNewAlignQuat);

                rotRollQuat.copy(rotNewAlignQuat).invert();
                rotRollQuat.premultiply(rotPartQuat);
                part.rotation = decomposeRollAroundAxis(rotRollQuat, rotNewAlignment);
            }

            // Batch instanceIds by articleId
            if (!grouped.has(articleId)) {
                grouped.set(articleId, { dragRef, instanceIds: [] });
            }
            grouped.get(articleId).instanceIds.push(instanceId);
        }

        const updateColliders = !state.isAutoDragActiveRef.current;
        for (const { dragRef, instanceIds } of grouped.values()) {
            if (!dragRef?.articleRef.current) {
                continue;
            }

            state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, instanceIds);
        }

        if (updateColliders) {
            const updatedInstances = collectUpdatedSelectedInstances(selectedInstanceIds, state);
            if (updatedInstances.length > 0) {
                state.updateArticleInstances(updatedInstances, false);
            }
        }

        state.toggleRecomputeTransformationBox();
        dragColliderCache.clear();
        invalidate();
    },

    copyArticleInstances: (showNotification = true) => {
        const state = get();
        const selectedInstanceIds = new Set(Object.keys(state.articleInstanceSelections));

        requestAnimationFrame(() => {
            state.setLastSelectionCache(state.articleInstanceSelections ?? {});
        });

        if (selectedInstanceIds.size === 0) {
            state.clipboard = [];
            return;
        }

        if (showNotification) {
            requestAnimationFrame(() => {
                set({
                    placementUpdate: { event: PlacementUpdateEvent.COPY, articleInstanceIds: selectedInstanceIds }
                });
            });
        }

        const poiNormalByGuid = new Map<string, { x: number; y: number; z: number }>();
        state.pois.forEach((poiData) => {
            const poiGuid = poiData.poi.poiGuid;
            const normal = getPoiNormal(poiData.poi);
            if (normal) {
                poiNormalByGuid.set(poiGuid, { x: normal.x, y: normal.y, z: normal.z });
            }
        });

        const entries: ArticleClipboardEntry[] = [];

        Object.entries(state.articles).forEach(([articleId, article]) => {
            Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                if (!selectedInstanceIds.has(instanceId)) {
                    return;
                }

                const parts = mapPartsRecordToArray(instance);
                if (parts.length === 0) {
                    return;
                }

                const groupId = state.instanceToGroupMap[instanceId];
                const group = groupId ? state.articleGroups[groupId] : undefined;
                const instancePoiGuids = instance.poiGuids?.filter((poiGuid) => poiGuid && poiGuid !== EMPTY_GUID);
                const resolvedPoiGuids = instancePoiGuids?.length
                    ? instancePoiGuids
                    : findPoiGuidsByInstanceId(state, instanceId);
                const instancePoiGuid = resolvedPoiGuids[0] ?? EMPTY_GUID;
                entries.push({
                    articleId,
                    parts,
                    poiGuids: [...new Set(resolvedPoiGuids)],
                    poiNormal: poiNormalByGuid.get(instancePoiGuid),
                    clipboardGroupId: group?.id,
                    clipboardGroupName: group?.name
                });
            });
        });

        set({ clipboard: entries });
    },

    pasteArticleInstances: (cursorPosition) => {
        const state = get();
        if (
            state.isViewMode ||
            state.isDraggingActive() ||
            state.isAutoDragActiveRef.current ||
            !state.clipboard.length
        ) {
            return;
        }

        // Synchronize raycastplane to cursor
        const clipboard = [...state.clipboard];
        const clipboardArticleIds = [...new Set(clipboard.map((entry) => entry.articleId))];
        const isFramiClampOnlyPaste =
            clipboardArticleIds.length > 0 &&
            clipboardArticleIds.every((articleId) => isFramiClamp(state.articles[articleId]?.articleNumber));
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const anchorInstanceId = selectedInstanceIds.length > 0 ? selectedInstanceIds[0] : undefined;
        const pointerConnectorArticleId = isFramiClampOnlyPaste ? getConnectorArticleId(state, anchorInstanceId) : null;
        const pointerManualInsertionPlane = pointerConnectorArticleId
            ? getManualPlacementPlane(state, pointerConnectorArticleId)
            : null;
        const preferredPointerPlane = getPreferredInsertionPlane(state, pointerManualInsertionPlane);

        const camera = state.mainCameraControlsRef.current?.camera;
        const raycaster = state.mainRaycasterRef.current;
        const insertionPlane = preferredPointerPlane ?? raycastPlane;
        if (cursorPosition && raycaster && camera) {
            raycaster.setFromCamera(cursorPosition, camera);
            if (raycaster.ray.intersectPlane(insertionPlane, projectedPoint)) {
                const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
                const alignment = isTopBottomView ? raycastPlane.normal : insertionPlane.normal;
                raycastPlane.setFromNormalAndCoplanarPoint(alignment, projectedPoint);
                state.setPointerPositionRef(projectedPoint);
            }
        }

        // Handle paste
        const anchorPartPos = getAnchorPartPosition(clipboard);
        const centroid = getClipboardCentroid(clipboard);
        const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
        const selectedPoiIndex = state.poiSelection.current.index;
        const isCornerPoiSelected =
            selectedPoiIndex !== undefined && state.pois[selectedPoiIndex]?.filterType === POIFilterType.Corner;
        const isCornerClipboard = clipboard.every((entry) =>
            entry.poiGuids.some((poiGuid) =>
                state.pois.some(
                    (poiData) => poiData.poi.poiGuid === poiGuid && poiData.filterType === POIFilterType.Corner
                )
            )
        );

        const isZAxisOnlyPaste = false;
        const hasMixedPoiClipboard = isMultiPOISelection(clipboard);
        const cornerPastePoiGuids = isCornerPoiSelected
            ? getRelatedPoiIndices(state.pois, selectedPoiIndex)
                  .map((poiIndex) => state.pois[poiIndex]?.poi.poiGuid)
                  .filter((poiGuid): poiGuid is string => Boolean(poiGuid))
            : undefined;

        const placeClipboard = (
            currentState: PlacementState,
            insertionPoint: Vector3,
            targetPoiNormal: Vector3,
            cornerCandidates?: PartPlacement[],
            cornerAnchorArticleId?: string
        ) => {
            const offset = getPasteOffset(anchorPartPos, insertionPoint, centroid);
            const { payload, selectedPoiInstanceIds, articleInstanceIds, pastedGroupMembers } = buildPastePayload(
                clipboard,
                currentState.articles,
                anchorPartPos,
                insertionPoint,
                offset,
                targetPoiNormal,
                hasMixedPoiClipboard || Boolean(cornerCandidates),
                isZAxisOnlyPaste,
                cornerPastePoiGuids
            );

            // Corner insertion replaces the payload anchor and rotates matching parts around it.
            if (cornerCandidates && cornerAnchorArticleId) {
                applyCornerPaste(payload, cornerAnchorArticleId, cornerCandidates);
            }

            // Keep the multi-POI source layout when the target placement requires it.
            finalizePaste(
                currentState,
                payload,
                articleInstanceIds,
                selectedPoiInstanceIds,
                pastedGroupMembers,
                isCornerClipboard
            );
        };

        if (selectedPoiIndex === undefined) {
            toastNotifications.information({ title: t('info'), message: t('infoSelectPOI') });
            return;
        }

        if (state.mainWebGLRendererRef.current) {
            state.mainWebGLRendererRef.current.domElement.style.cursor = 'wait';
            invalidate();
        }

        // Connector paste
        if (isFramiClampOnlyPaste) {
            const insertionArticleId = clipboardArticleIds[0];
            if (!insertionArticleId) {
                return;
            }

            if (state.selectedArticleId !== insertionArticleId) {
                state.setSelectedArticleId(insertionArticleId);
            }

            void (async () => {
                await get().prepareManualConnectorDrag?.({
                    articleId: insertionArticleId,
                    articleNumber: state.articles[insertionArticleId]?.articleNumber ?? null,
                    poiIndex: selectedPoiIndex
                });

                const currentState = get();
                const insertionPlane = currentState.manualPlacementData.plane;
                const connectorCandidates = currentState.manualPlacementData.candidates;
                if (
                    currentState.isViewMode ||
                    currentState.poiSelection.current.index !== selectedPoiIndex ||
                    currentState.selectedArticleId !== insertionArticleId ||
                    currentState.manualPlacementData.articleId !== insertionArticleId ||
                    !insertionPlane ||
                    connectorCandidates.length === 0
                ) {
                    if (currentState.mainWebGLRendererRef.current) {
                        currentState.mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
                    }
                    return;
                }

                if (
                    updateCursorRay(currentState) &&
                    currentState.mainRaycasterRef.current?.ray.intersectPlane(insertionPlane, projectedPoint)
                ) {
                    currentState.setPointerPositionRef(projectedPoint);
                }

                const anchorCandidate = getNearestSnapCandidate(
                    connectorCandidates,
                    currentState.pointerPositionRef.current
                );
                if (!anchorCandidate) {
                    return;
                }

                anchorPoint.set(anchorCandidate.position.x, anchorCandidate.position.y, anchorCandidate.position.z);
                const insertionPoint = anchorPoint.clone();
                insertionPlane.projectPoint(insertionPoint, insertionPoint);
                const cursorZ = currentState.pointerPositionRef.current?.z;
                if (isZAxisOnlyPaste && cursorZ !== undefined) {
                    insertionPoint.z = cursorZ;
                }

                if (isTopBottomView) {
                    insertionPoint.z = (anchorPartPos ?? centroid).z;
                }

                raycastPlane.setFromNormalAndCoplanarPoint(
                    isTopBottomView ? raycastPlane.normal : insertionPlane.normal,
                    insertionPoint
                );

                currentState.pointerPositionRef.current = insertionPoint.clone();
                const targetPoiNormal = insertionPlane.normal.clone();
                if (targetPoiNormal) {
                    placeClipboard(currentState, insertionPoint, targetPoiNormal);
                }
            })();

            return;
        }

        if (isCornerPoiSelected && isCornerClipboard) {
            const insertionArticleId = clipboardArticleIds[0];
            const prepareCornerInsertion = state.prepareManualCornerInsertion;
            if (!insertionArticleId || !prepareCornerInsertion) {
                return;
            }

            if (state.selectedArticleId !== insertionArticleId) {
                state.setSelectedArticleId(insertionArticleId);
            }

            void (async () => {
                await prepareCornerInsertion({
                    articleId: insertionArticleId,
                    articleNumber: state.articles[insertionArticleId]?.articleNumber ?? null,
                    poiIndex: selectedPoiIndex
                });

                const currentState = get();
                const insertionPlane = currentState.manualPlacementData.plane;
                const cornerCandidates = currentState.manualPlacementData.candidates;
                if (
                    currentState.isViewMode ||
                    currentState.poiSelection.current.index !== selectedPoiIndex ||
                    currentState.selectedArticleId !== insertionArticleId ||
                    currentState.manualPlacementData.articleId !== insertionArticleId ||
                    currentState.manualPlacementData.poiIndex !== selectedPoiIndex ||
                    !insertionPlane ||
                    cornerCandidates.length === 0
                ) {
                    if (currentState.mainWebGLRendererRef.current) {
                        currentState.mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
                    }
                    return;
                }

                if (
                    updateCursorRay(currentState) &&
                    currentState.mainRaycasterRef.current?.ray.intersectPlane(insertionPlane, projectedPoint)
                ) {
                    currentState.setPointerPositionRef(projectedPoint);
                }
                const cursorZ = currentState.pointerPositionRef.current?.z;
                const anchorCandidate =
                    getPlacementAnchorPart(cornerCandidates, clipboard[0]?.parts[0]) ?? cornerCandidates[0];
                const zOffset = isTopBottomView || cursorZ === undefined ? 0 : cursorZ - anchorCandidate.position.z;
                const adjustedCornerCandidates = clonePartPlacementsWithZOffset(cornerCandidates, zOffset);
                const adjustedAnchorCandidate =
                    getPlacementAnchorPart(adjustedCornerCandidates, clipboard[0]?.parts[0]) ??
                    adjustedCornerCandidates[0];
                anchorPoint.set(
                    adjustedAnchorCandidate.position.x,
                    adjustedAnchorCandidate.position.y,
                    adjustedAnchorCandidate.position.z
                );
                raycastPlane.setFromNormalAndCoplanarPoint(
                    isTopBottomView ? raycastPlane.normal : insertionPlane.normal,
                    anchorPoint
                );
                currentState.pointerPositionRef.current = anchorPoint.clone();
                placeClipboard(
                    currentState,
                    anchorPoint,
                    insertionPlane.normal,
                    adjustedCornerCandidates,
                    insertionArticleId
                );
            })();

            return;
        }

        // Default paste
        const targetPoiNormal =
            state.workingPlane?.normal.clone() ??
            getPoiNormal(state.pois[selectedPoiIndex]?.poi) ??
            Object3D.DEFAULT_UP.clone();

        const insertionPoint = getInsertionPoint(state, anchorPartPos, centroid);

        if (state.workingPlane) {
            state.workingPlane.projectPoint(insertionPoint, insertionPoint);
        }

        if (isTopBottomView) {
            insertionPoint.z = (anchorPartPos ?? centroid).z;
        }

        raycastPlane.setFromNormalAndCoplanarPoint(
            isTopBottomView ? raycastPlane.normal : targetPoiNormal,
            insertionPoint
        );

        placeClipboard(state, insertionPoint, targetPoiNormal);
    },

    cutArticleInstances: () => {
        const state = get();
        if (state.isViewMode) {
            return;
        }
        state.copyArticleInstances(false);
        state.discardArticleInstanceSelection(true);
    }
});
